import styled from '@emotion/styled';
import Avatar from 'presentations/views/components/atoms/Avatar';
import Image from 'presentations/views/components/atoms/Image';
import { borderRadius, mediaMax, spacing } from 'presentations/views/styles';
import {
  blueGrayPale,
  gray04,
  primary,
  white,
  whiteFix,
} from 'presentations/views/styles/color';
import { imagePath } from 'presentations/assets';
import { ViewerItem } from 'presentations/api/schema/user';

interface Props {
  viewer: ViewerItem;
  isThinking: boolean;
  messageStream: string | null;
  messages: { role: string; content: string }[];
}

export default function Component(props: Props) {
  return (
    <List>
      {props.isThinking && (
        <ListItem>
          <HostAnimateAvatar>
            <HostAnimateImage src={imagePath('assistant/icon.svg')} alt="" />
          </HostAnimateAvatar>
          <HostComment>
            <LoadingDotsContainer>
              <LoadingDots />
            </LoadingDotsContainer>
          </HostComment>
        </ListItem>
      )}
      {props.messageStream && (
        <ListItem>
          <HostAnimateAvatar>
            <HostAnimateImage src={imagePath('assistant/icon.svg')} alt="" />
          </HostAnimateAvatar>
          <HostComment
            dangerouslySetInnerHTML={{ __html: `${props.messageStream}` }}
          />
        </ListItem>
      )}
      {[...props.messages].reverse().map((message, index) => (
        <>
          {message.role === 'system' ? (
            <ListItem key={index}>
              <HostAnimateAvatar>
                <HostAnimateImage
                  src={imagePath('assistant/icon.svg')}
                  alt=""
                />
              </HostAnimateAvatar>
              <HostComment
                dangerouslySetInnerHTML={{ __html: message.content }}
              />
            </ListItem>
          ) : (
            <ListItem key={index} isGuest>
              <GuestAvatar src={imagePath(props.viewer.profileImage!)} />
              <GuestComment
                dangerouslySetInnerHTML={{ __html: message.content }}
              />
            </ListItem>
          )}
        </>
      ))}
      {/* <ListItem isGuest>
        <GuestAvatar src={imagePath(props.viewer.profileImage!)} />
        <GuestComment>{t(`first message`)}</GuestComment>
      </ListItem> */}
    </List>
  );
}

export const MessageListMain = styled.div`
  display: flex;
  flex-direction: column-reverse;
  height: calc(100vh / 5 * 3);
  overflow-y: auto;
  padding: ${spacing(16)} 0;
  ${mediaMax.tablet} {
    height: initial;
    overflow-y: hidden;
  }
`;

const List = styled.div`
  width: 100%;
  padding: 0 ${spacing(12)};
  background-color: ${blueGrayPale};
  display: flex;
  flex-direction: column-reverse;
`;

const ListItem = styled.div<{ isGuest?: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.isGuest ? 'row-reverse' : 'row')};
  margin-bottom: ${spacing(16)};
`;
const HostAnimateImage = styled(Image)`
  width: 20px;
  height: 20px;
`;
const HostAnimateAvatar = styled.div`
  margin-right: ${spacing(8)};
  border: 1px solid ${primary};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${whiteFix};
`;
const HostComment = styled.div`
  background-color: ${white};
  flex: 1;
  border-radius: 0 ${borderRadius(16)} ${borderRadius(16)} ${borderRadius(16)};
  box-shadow: 0px 8px 20px 0px rgba(0, 0, 0, 0.05);
  width: 100%;
  padding: ${spacing(16)} ${spacing(12)};
  /* white-space: pre-wrap; */
  ul, ol {
    background-color: ${blueGrayPale};
    padding: 0 ${spacing(12)};
    border-radius: ${borderRadius(12)};
    margin: ${spacing(12)} 0;
    li {
      padding: ${spacing(8)} 0;
      border-bottom: 1px solid ${gray04};
      :last-child {
        border-bottom: 0;
      }
    }
  }
  ol {
    list-style-type: decimal;
    list-style-position: inside;
  }
  /* ul {
    list-style-type: disc;
    list-style-position: inside;
    padding-inline-start: ${spacing(12)};
    margin-top: ${spacing(8)};
    li {
      margin-bottom: ${spacing(8)};
    }
  } */
  /* ol {
    list-style-type: decimal;
    list-style-position: inside;
    li {
      margin-bottom: ${spacing(8)};
    }
  } */
  ul,
  ol {
    p {
      display: inline;
    }
  }
  p {
    margin-bottom: ${spacing(8)};
  }
`;
const GuestAvatar = styled(Avatar)`
  margin-left: ${spacing(8)};
`;
const GuestComment = styled.div`
  background-color: ${primary};
  color: ${whiteFix};
  border-radius: ${borderRadius(16)} 0 ${borderRadius(16)} ${borderRadius(16)};
  width: 100%;
  padding: ${spacing(16)} ${spacing(12)};
  white-space: pre-wrap;
`;
const LoadingDotsContainer = styled.div`
  display: flex;
  align-items: center;
  padding-left: ${spacing(20)};
`;
const LoadingDots = styled.div`
  position: relative;
  left: -9999px;
  width: 8px;
  height: 8px;
  border-radius: 4px;
  background-color: ${primary};
  color: ${primary};
  box-shadow: 9999px 0 0 0 ${primary};
  animation: dot-falling 1s infinite linear;
  animation-delay: 0.1s;
  &::before {
    content: '';
    display: inline-block;
    position: absolute;
    top: 0;

    width: 8px;
    height: 8px;
    border-radius: 4px;
    background-color: ${primary};
    color: ${primary};
    animation: dot-falling-before 1s infinite linear;
    animation-delay: 0s;
  }
  &::after {
    content: '';
    display: inline-block;
    position: absolute;
    top: 0;

    width: 8px;
    height: 8px;
    border-radius: 4px;
    background-color: ${primary};
    color: ${primary};
    animation: dot-falling-after 1s infinite linear;
    animation-delay: 0.2s;
  }

  @keyframes dot-falling {
    0% {
      box-shadow: 9999px -7px 0 0 rgba(152, 128, 255, 0);
    }
    25%,
    50%,
    75% {
      box-shadow: 9999px 0 0 0 ${primary};
    }
    100% {
      box-shadow: 9999px 7px 0 0 rgba(152, 128, 255, 0);
    }
  }
  @keyframes dot-falling-before {
    0% {
      box-shadow: 9984px -7px 0 0 rgba(152, 128, 255, 0);
    }
    25%,
    50%,
    75% {
      box-shadow: 9984px 0 0 0 ${primary};
    }
    100% {
      box-shadow: 9984px 7px 0 0 rgba(152, 128, 255, 0);
    }
  }
  @keyframes dot-falling-after {
    0% {
      box-shadow: 10014px -7px 0 0 rgba(152, 128, 255, 0);
    }
    25%,
    50%,
    75% {
      box-shadow: 10014px 0 0 0 ${primary};
    }
    100% {
      box-shadow: 10014px 7px 0 0 rgba(152, 128, 255, 0);
    }
  }
`;
